/**
 * Link Detection Service
 * Utilities for detecting external links, URL sanitization, and security attributes
 */

/**
 * Determines if a URL is external to the current domain
 * @param {string} url - The URL to check
 * @param {string} currentDomain - The current domain (defaults to window.location.hostname)
 * @returns {boolean} True if the link is external
 */
export const isExternalLink = (url, currentDomain = null) => {
  try {
    // Handle relative URLs (always internal)
    if (!url || url.startsWith('/') || url.startsWith('#') || url.startsWith('?')) {
      return false;
    }

    // Handle mailto, tel, and other non-http protocols (not external navigation)
    if (url.startsWith('mailto:') || url.startsWith('tel:') || url.startsWith('sms:')) {
      return false;
    }

    // Get current domain
    const domain = currentDomain || (typeof window !== 'undefined' ? window.location.hostname : '');
    
    // Try to parse as absolute URL first
    let linkUrl;
    try {
      linkUrl = new URL(url);
    } catch (error) {
      // If it fails to parse as absolute URL and doesn't look like a relative path,
      // treat as external for safety
      if (!url.startsWith('/') && !url.startsWith('#') && !url.startsWith('?')) {
        console.warn('Failed to parse URL:', url, error);
        return true;
      }
      
      // Try with base URL for relative-like URLs
      try {
        linkUrl = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://example.com');
      } catch (secondError) {
        console.warn('Failed to parse URL:', url, secondError);
        return true;
      }
    }
    
    // Compare hostnames (case-insensitive)
    const linkHostname = linkUrl.hostname.toLowerCase();
    const currentHostname = domain.toLowerCase();
    
    // Check if it's the same domain or subdomain
    return linkHostname !== currentHostname && !linkHostname.endsWith('.' + currentHostname);
  } catch (error) {
    // If URL parsing fails, treat as external for safety
    console.warn('Failed to parse URL:', url, error);
    return true;
  }
};

/**
 * Sanitizes a URL to prevent XSS attacks
 * @param {string} url - The URL to sanitize
 * @returns {string} The sanitized URL
 */
export const sanitizeUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  const trimmedUrl = url.trim();

  // Handle relative URLs first (return as-is, they're safe)
  if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('#') || trimmedUrl.startsWith('?')) {
    return trimmedUrl;
  }

  // Remove dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerUrl = trimmedUrl.toLowerCase();
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      console.warn('Blocked dangerous URL:', url);
      return '';
    }
  }

  // Allow only safe protocols
  const safeProtocols = ['http:', 'https:', 'mailto:', 'tel:', 'sms:'];
  
  try {
    const urlObj = new URL(trimmedUrl);
    
    if (!safeProtocols.includes(urlObj.protocol)) {
      console.warn('Blocked unsafe protocol:', urlObj.protocol);
      return '';
    }
    
    return urlObj.href;
  } catch (error) {
    // If URL parsing fails, it's likely malformed - reject for safety
    console.warn('Failed to sanitize URL:', url, error);
    return '';
  }
};

/**
 * Adds security attributes to an anchor element for external links
 * @param {HTMLAnchorElement} element - The anchor element to modify
 */
export const addSecurityAttributes = (element) => {
  if (!element || element.tagName !== 'A') {
    return;
  }

  const href = element.getAttribute('href');
  if (!href || !isExternalLink(href)) {
    return;
  }

  // Add security attributes for external links
  element.setAttribute('rel', 'noopener noreferrer');
  element.setAttribute('target', '_blank');
};

/**
 * Analyzes a link and returns detailed information
 * @param {string} url - The URL to analyze
 * @param {string} currentDomain - The current domain
 * @returns {Object} Link analysis result
 */
export const analyzeLinkUrl = (url, currentDomain = null) => {
  const sanitizedUrl = sanitizeUrl(url);
  const isExternal = isExternalLink(url, currentDomain);
  
  let domain = '';
  let protocol = '';
  
  try {
    const urlObj = new URL(sanitizedUrl, typeof window !== 'undefined' ? window.location.origin : 'https://example.com');
    domain = urlObj.hostname;
    protocol = urlObj.protocol;
  } catch (error) {
    // For relative URLs or parsing errors
    domain = currentDomain || (typeof window !== 'undefined' ? window.location.hostname : '');
    protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  }
  
  return {
    isExternal,
    sanitizedUrl,
    domain,
    protocol,
    originalUrl: url
  };
};

/**
 * Opens a URL in a new tab with security attributes
 * @param {string} url - The URL to open
 */
export const openExternalLink = (url) => {
  const sanitizedUrl = sanitizeUrl(url);
  if (!sanitizedUrl) {
    console.error('Cannot open unsafe URL:', url);
    return;
  }

  if (typeof window !== 'undefined') {
    const newWindow = window.open(sanitizedUrl, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      console.warn('Popup blocked or failed to open:', sanitizedUrl);
    }
  }
};